import moment from 'moment';
import clone from 'lodash.clonedeep';

import { GRAPH_COLORS } from '../constants';
import { getSortedDates, dateToNum, getDateFromString } from './format.util';

const VOLUME_ERA_1_END = moment(new Date('11/30/2021'));
const VOLUME_ERA_2_END = moment(new Date('12/31/2023'));
export function simplifyCoordinates(coordinates, totalPoints = 100) {
  if (coordinates.length < 100) {
    return coordinates;
  }

  const pickRange = Math.ceil(coordinates.length / totalPoints);

  let min = 0;
  let max = 0;

  for (let i = 0; i < coordinates.length; i += 1) {
    if (coordinates[min].y > coordinates[i].y) {
      min = i;
    }

    if (coordinates[max].y < coordinates[i].y) {
      max = i;
    }
  }

  function filterData(i) {
    return i % pickRange === 0
      || i === min
      || i === max
      || i === 0
      || i === coordinates.length - 1;
  }

  return coordinates.filter((_, i) => filterData(i));
}

export function getSalesGraphData(card, zoom, simplify = false) {
  const labels = [];
  const prices = [];
  const pointColors = [];
  const timelineData = [];
  const dailySales = clone(card.dailySales) || {};

  let coordinates = [];

  if (dailySales) {
    if (card.collectionCardId && (card.cardId || card.useSalesForValue || card.useGradedCardAutomatic)) {
      if (card.willManageQuantity && card.individualCards && card.individualCards.length > 0) {
        card.individualCards.forEach((individualCard) => {
          if (individualCard.datePurchased && typeof individualCard.investment === 'number') {
            dailySales[moment(individualCard.datePurchased).format('MM/DD/YYYY')] = { n: 1, p: individualCard.investment, c: true };
          }
        });
      } else if (card.datePurchased && typeof card.investment === 'number') {
        dailySales[moment(card.datePurchased).format('MM/DD/YYYY')] = { n: 1, p: card.investment, c: true };
      }
    }

    const sortedDates = getSortedDates(dailySales, zoom);

    sortedDates.forEach((d) => {
      let pointColor = '#34BE8B';

      if (dailySales[d].c) {
        pointColor = '#FF9957';
      } else if (dailySales[d].o) {
        pointColor = '#3398DB';
      } else if (dailySales[d].w) {
        pointColor = '#FFD64D';
      }

      timelineData.push({ date: d, price: dailySales[d].p, num: dailySales[d].n });
      labels.push(d);
      prices.push(dailySales[d].p);
      pointColors.push(pointColor);
      coordinates.push({
        x: getDateFromString(d),
        y: dailySales[d].p,
        c: dailySales[d].c || false,
        o: dailySales[d].o || false,
        w: dailySales[d].w || false,
      });
    });
  }

  if (simplify) {
    coordinates = simplifyCoordinates(coordinates);
  }

  const dataset = {
    label: card.label,
    borderColor: '#34BE8B',
    pointRadius: 2,
    borderWidth: 3,
    fill: false,
    spanGaps: true,
    data: prices,
    pointBorderColor: pointColors,
  };

  const timelineStartPrice = timelineData.length > 0 ? timelineData[0].price : 0;
  const timelineCurrentPrice = timelineData.length > 0 ? timelineData[timelineData.length - 1].price : 0;

  const response = {
    timelineData: {
      hasData: timelineData.length > 0,
      timelineStartPrice,
      timelineCurrentPrice,
      timelineHighPrice: Math.max(...timelineData.map((data) => data.price)),
      timelineLowPrice: Math.min(...timelineData.map((data) => data.price)),
      timelineRoi: timelineData.length < 2 || timelineStartPrice === 0 ? 0 : (timelineCurrentPrice - timelineStartPrice) / timelineStartPrice,
      timelineDollarChange: timelineData.length < 2 ? 0 : timelineCurrentPrice - timelineStartPrice,
      timelineAveragePrice: timelineData.length ? (timelineData.reduce((total, sale) => total + sale.price, 0)) / timelineData.length : 0,
      timelineNumSales: timelineData.reduce((total, sale) => total + sale.num, 0),
    },
  };

  if (simplify) {
    response.coordinates = coordinates;
  } else {
    response.labels = labels;
    response.datasets = [dataset];
  }

  return response;
}

/**
 * Keys from `monthlyVolumeTotal` in cron use `MM/YYYY` (e.g. `04/2026`).
 * Graph components expect first-of-month labels `MM/01/YYYY`.
 */
export function monthlyVolumeTotalKeyToGraphLabel(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') {
    return null;
  }

  const parts = monthKey.split('/');

  if (parts.length === 2 && parts[0].length === 2 && parts[1].length === 4) {
    return `${parts[0]}/01/${parts[1]}`;
  }

  if (parts.length === 3) {
    return monthKey;
  }

  if (/^\d{6}$/.test(monthKey)) {
    return `${monthKey.slice(0, 2)}/01/${monthKey.slice(2)}`;
  }

  return null;
}

/** Same era coloring as `getIndustryData` volume graphs (Industry monthly volume). */
export function volumeGraphPointColorForLabel(dateLabel) {
  const date = moment(new Date(dateLabel));

  let pointColor = GRAPH_COLORS[0];

  if (date.isBetween(VOLUME_ERA_1_END, VOLUME_ERA_2_END)) {
    pointColor = GRAPH_COLORS[2];
  } else if (date.isAfter(VOLUME_ERA_2_END)) {
    pointColor = '#3398DC';
  }

  return pointColor;
}
